import React, { useState, useEffect } from "react";
import {
  Box,
  SelectChangeEvent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  styled,
} from "@mui/material";
import { useSchedule } from "../../contexts/ScheduleContext";
import { useClub } from "../../contexts/ClubContext";
import { Club } from "../../types/ClubTypes";
import { Game } from "../../types/ScheduleTypes";
import { ScheduleCard } from "./ScheduleCard";

const columnWidths = {
  date: 180,
  location: 100,
  opponent: 450,
  result: 80,
  score: 100,
};

const StyledContent = styled("div")(({ theme }) => ({
  padding: theme.spacing(3),
  color: theme.palette.text.primary,
}));

export const Schedule = () => {
  const { club } = useClub();
  const { games, clubs, fetchGames } = useSchedule();
  const [clubId, setClubId] = useState<number | null>(club?.id || null);

  useEffect(() => {
    if (club) {
      setClubId(club.id);
    }
  }, [club]);

  const sortedGames = games.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  });

  const handleClubChange = (event: SelectChangeEvent<string>) => {
    setClubId(Number(event.target.value));
    fetchGames(Number(event.target.value));
  };

  const didClubWin = (game: Game) => {

    if (game.is_scrimmage) {
      return "Scrimmage";
    }

    const isHome = game.home_club.id === clubId;
    const clubScore = isHome ? game.home_score : game.away_score;
    const opponentScore = isHome ? game.away_score : game.home_score;

    if (clubScore === null || opponentScore === null) {
      return "";
    }

    if (clubScore > opponentScore) {
      return "W";
    } else if (clubScore < opponentScore) {
      return "L";
    } else {
      return "T";
    }
  };

  return (
    <StyledContent>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "& .MuiInputLabel-root": {
            color: "text.secondary",
          },
          "& .MuiOutlinedInput-root": {
            color: "text.primary",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Club</InputLabel>
            <Select
              value={clubId?.toString()}
              label="Club"
              onChange={handleClubChange}
              sx={{
                "& .MuiSelect-icon": {
                  color: "text.secondary",
                },
              }}
            >
              {clubs?.map((club: Club) => (
                <MenuItem key={club.id} value={club.id.toString()}>
                  {club.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        {sortedGames?.map((game) => (
          <ScheduleCard
            key={game.id}
            game={game}
            clubId={clubId as number}
            didClubWin={didClubWin}
          />
        ))}
      </Box>
    </StyledContent>
  );
};
